import { useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from 'react-use';
import {
  UserSettings,
  useStoreSettingsApi,
} from '@/webapi/use-store-settings-api';
import { getCoPilotModeConfig } from '@/features/account-context';

export type CopilotMode = `Build` | `Analyze`;

export const ANALYZE_AGENT_ID = `csm-bi-analyst`;

export const COPILOT_MODE_STORAGE_KEY = `vsly:copilot-mode`;

const isCopilotMode = (value: unknown): value is CopilotMode =>
  value === `Build` || value === `Analyze`;

const getCopilotMode = (
  settings: UserSettings | undefined,
): CopilotMode | undefined => {
  const mode = settings?.userSettings?.copilotMode;
  return isCopilotMode(mode) ? mode : undefined;
};

const hasStoredCopilotMode = () => {
  try {
    return isCopilotMode(
      JSON.parse(window.localStorage.getItem(COPILOT_MODE_STORAGE_KEY)),
    );
  } catch {
    return false;
  }
};

export function useCopilotMode(forcedDefaultMode?: CopilotMode) {
  const modeCfg = getCoPilotModeConfig();
  const hadStoredModeOnMountRef = useRef<boolean>();
  if (hadStoredModeOnMountRef.current === undefined) {
    hadStoredModeOnMountRef.current = hasStoredCopilotMode();
  }
  const [storedCopilotMode, setCopilotMode] = useLocalStorage<CopilotMode>(
    COPILOT_MODE_STORAGE_KEY,
    modeCfg.default,
  );
  const settingsRef = useRef<UserSettings>();
  const hasLoadedSettingsRef = useRef(false);
  const selectedModeRef = useRef<CopilotMode>();
  const copilotMode =
    forcedDefaultMode && !selectedModeRef.current
      ? forcedDefaultMode
      : storedCopilotMode;

  const { getUserSettings, setUserSettings } = useStoreSettingsApi();

  const persistCopilotMode = useCallback(
    (mode: CopilotMode) => {
      const currentSettings = settingsRef.current;
      return setUserSettings({
        ...currentSettings,
        isConfidenceIntervalsEnabled:
          currentSettings?.isConfidenceIntervalsEnabled || `NO`,
        userSettings: {
          ...currentSettings?.userSettings,
          copilotMode: mode,
        },
      });
    },
    [setUserSettings],
  );

  useEffect(() => {
    let isActive = true;
    hasLoadedSettingsRef.current = false;
    getUserSettings()
      .then((settings) => {
        if (!isActive) return;

        settingsRef.current = settings;
        hasLoadedSettingsRef.current = true;
        if (selectedModeRef.current) {
          persistCopilotMode(selectedModeRef.current).catch(() => undefined);
          return;
        }

        const storedMode = getCopilotMode(settings);
        if (storedMode) setCopilotMode(storedMode);
      })
      .catch(() => undefined);

    return () => {
      isActive = false;
    };
  }, [getUserSettings, persistCopilotMode, setCopilotMode]);

  // `modeCfg.default` is derived from account/feature-bit data that may still
  // be loading on first mount. Re-apply it once it becomes available so a
  // first-time user doesn't get stuck on the fallback mode picked before
  // that data arrived, as long as they haven't chosen a mode themselves and
  // there is no saved preference on the server.
  useEffect(() => {
    if (!modeCfg.default) return;
    if (selectedModeRef.current) return;
    if (hadStoredModeOnMountRef.current) return;
    if (hasLoadedSettingsRef.current && getCopilotMode(settingsRef.current))
      return;

    setCopilotMode(modeCfg.default);
  }, [modeCfg.default, setCopilotMode]);

  const selectCopilotMode = useCallback(
    (mode: CopilotMode) => {
      selectedModeRef.current = mode;
      setCopilotMode(mode);
      if (hasLoadedSettingsRef.current) {
        persistCopilotMode(mode).catch(() => undefined);
      }
    },
    [persistCopilotMode, setCopilotMode],
  );

  return { copilotMode: copilotMode || `Build`, selectCopilotMode };
}
